import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIndustry,
  faArchive,
  faEarthAmericas,
  faClock,
  faGears,
  faAppleWhole,
  faWheatAwn,
  faTrash,
  faUsers,
  faShield,
  faCloudRain,
} from '@fortawesome/free-solid-svg-icons';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import navigateToAqila1 from '../../../utils/navigate-to-aqila1';
import { ParametroService, PermissoesService } from '../../../services';
import useLocalStore from '../../../hooks/use-local-store';
import useAsyncFetch from '../../../hooks/use-async';

const LeftMenu: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const permissoes = PermissoesService;
  const isPropriedadeSelecionada = Boolean(
    useLocalStore('currentPropriedade').value,
  );
  const user = useLocalStore('user').value;

  const { data: parametro } = useAsyncFetch(
    {
      callback: () => ParametroService.parametro(),
      errorMessage: 'Falha ao carregar os parâmetros.',
      withLoader: true,
      onError: () => navigate('/'),
    },
    [],
  );

  const isMasterUser = useMemo(
    () => user?.nome.toLowerCase() === 'inovacao',
    [user],
  );

  const handleClassName = useCallback(
    (shouldBeShown?: boolean, rota?: string) => {
      let className = '';

      if (!shouldBeShown) className += 'hidden';

      if (rota && pathname.includes(rota)) className += ' active';
      return className;
    },
    [pathname],
  );

  const items: MenuItem[] = useMemo(
    () => [
      {
        className: 'hidden',
        items: [
          {
            label: 'Depósitos',
            icon: <FontAwesomeIcon icon={faIndustry} />,
            command: () => navigateToAqila1('depositos'),
            className: handleClassName(permissoes.depositos.read),
          },
          {
            label: 'Itens de Estoque',
            icon: <FontAwesomeIcon icon={faArchive} />,
            command: () => navigateToAqila1('itens_estoque'),
            className: handleClassName(
              permissoes.itensEstoque.read && isPropriedadeSelecionada,
            ),
          },
          {
            label: 'Máquinas',
            icon: <i className="icon-fulltrator" />,
            command: () => navigateToAqila1('maquinas'),
            className: handleClassName(permissoes.maquina.read),
          },
          {
            label: 'Propriedades',
            icon: <FontAwesomeIcon icon={faEarthAmericas} />,
            command: () => navigateToAqila1('cadastro_propriedades'),
            className: handleClassName(permissoes.propriedades.read),
          },
          {
            label: 'Safras',
            icon: <FontAwesomeIcon icon={faClock} />,
            command: () => navigateToAqila1('safras'),
            className: handleClassName(
              permissoes.safras.read && parametro?.naoClienteAgrogestao,
            ),
          },
          {
            label: 'Atividades',
            icon: <i className="icon-stadium" />,
            command: () => navigate('/gerenciador-dados/atividades'),
            className: handleClassName(
              (permissoes.atividade.read || permissoes.atividade.manage) &&
                parametro?.naoClienteAgrogestao,
              '/atividades',
            ),
          },
          {
            label: 'Estádios Fenológicos',
            icon: <FontAwesomeIcon icon={faWheatAwn} />,
            command: () => navigateToAqila1('estadios_fenologicos'),
            className: handleClassName(
              permissoes.estadiosFenologicos.read &&
                parametro?.naoClienteAgrogestao,
            ),
          },
          {
            label: 'Talhões',
            icon: <i className="icon-layer" />,
            command: () => navigateToAqila1('glebas'),
            className: handleClassName(permissoes.glebas.read),
          },
          {
            label: 'Operações de Campo',
            icon: <FontAwesomeIcon icon={faGears} />,
            command: () => navigate('/gerenciador-dados/operacoes'),
            className: handleClassName(
              (permissoes.operacao.read || permissoes.operacao.manage) &&
                parametro?.naoClienteAgrogestao,
              '/operacoes',
            ),
          },
          {
            label: 'Colheitas',
            icon: <FontAwesomeIcon icon={faAppleWhole} />,
            command: () => navigateToAqila1('colheitas'),
            className: handleClassName(
              permissoes.colheitas.read &&
                parametro?.naoClienteAgrogestao &&
                isPropriedadeSelecionada,
            ),
          },
          {
            label: 'Pluviômetros',
            icon: <FontAwesomeIcon icon={faCloudRain} />,
            command: () => navigate('/gerenciador-dados/pluviometros'),
            className: handleClassName(
              (permissoes.pluviometro.read || permissoes.pluviometro.manage) &&
                parametro?.naoClienteAgrogestao,
              '/pluviometros',
            ),
          },
          {
            label: 'Tipos de Ocorrências',
            icon: <i className="icon-ocurrences" />,
            command: () => navigateToAqila1('tipos_ocorrencias'),
            className: handleClassName(permissoes.tiposOcorrencia.read),
          },
          {
            label: 'Plantios',
            icon: <i className="icon-semeadura" />,
            command: () => navigateToAqila1('plantios'),
            className: handleClassName(
              permissoes.plantio.read && isPropriedadeSelecionada,
            ),
          },
          {
            label: 'Apagar Dados',
            icon: <FontAwesomeIcon icon={faTrash} />,
            command: () => navigateToAqila1('delete-data'),
            className: handleClassName(
              isMasterUser && isPropriedadeSelecionada,
            ),
          },
        ],
      },
      {
        label: 'Acessos',
        items: [
          {
            label: 'Usuários',
            icon: <FontAwesomeIcon icon={faUsers} />,
            command: () => navigateToAqila1('usuarios'),
            className: handleClassName(permissoes.users.manage),
          },
          {
            label: 'Permissões de Acesso',
            icon: <FontAwesomeIcon icon={faShield} />,
            command: () => navigateToAqila1('funcoes'),
            className: handleClassName(permissoes.roles.read),
          },
        ],
      },
      {
        className: 'hidden',
        items: [
          {
            label: 'Selecione uma propriedade para exibir mais opções',
            className: `warning ${isPropriedadeSelecionada ? 'hidden' : ''}`,
          },
        ],
      },
    ],
    [
      navigate,
      handleClassName,
      permissoes,
      isPropriedadeSelecionada,
      isMasterUser,
      parametro?.naoClienteAgrogestao,
    ],
  );

  return (
    <div className="menu-container">
      <Menu model={items} className="menu" />
    </div>
  );
};

export default LeftMenu;
